/** Finance surface — the income statement for the selected period, plus
 * what still needs a decision (Needs you) and the disclosures behind it. */
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AppShell } from '../../components/AppShell';
import { CompleteMonthRangeControls } from '../../components/CompleteMonthRangeControls';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import { useMe, useModuleEnabled } from '../../lib/auth';
import {
  defaultFinancePeriod,
  expenseAttestationDialogCopy,
  expenseAttestationMonthLabel,
  expenseAttestationMonthStatuses,
  financeAttestedMonthsFootnote,
  financeCoverageCopy,
  financeCategoryLabel,
  financeDateBasisLabel,
  financeMonthName,
  financePayoutReconciliationCopy,
  financePeriodMonthKeys,
  financePeriodRangeLabel,
  financeSettlementHeaderCopy,
  financeSourceCostLabel,
  financeSourceEventLabel,
  financeStatusSentence,
  financeWarehouseLinkCopy,
  formatFinanceCurrency,
  formatFinanceDate,
  formatFinanceDelta,
  formatFinanceSourceCost,
  formatFinanceSourceUnits,
  formatRelativeTime,
  loadPayoutsOpen,
  payoutDateCopy,
  payoutSectionSummary,
  payoutStatusPlainLabel,
  savePayoutsOpen,
  businessPositionGradeLabel,
  businessPositionSinceAsOfLabel,
  cogsReplanProgressLabel,
  transitionFinancePeriod,
  useBusinessPosition,
  useCogsReplanState,
  useFinanceSummary,
  useFinanceSummarySources,
  useRequestCogsReplan,
  type FinancePeriod,
} from '../../lib/finance-summary';
import { canAccessFeeCenter, formatFeeCenterDate } from '../../lib/fee-center';
import { formatCategory as formatOperatingExpenseCategory } from '../../lib/operating-expenses';
import { ApiCallError } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '../../design';
import type {
  BusinessPositionResponse,
  CogsReplanState,
  FinanceSummaryPayout,
  FinanceSummarySettlementActivity,
  FinanceSummarySource,
} from '@sourcito/schemas';
import {
  formatWarehouseCents,
  invoiceStatusLabel,
  resolveWarehouseFacility,
  useAttestWarehousePeriodExpenses,
  useCreateWarehouseCreditNote,
  useRecordWarehouseInvoicePayment,
  useReverseWarehouseInvoicePayment,
  useTransitionWarehouseInvoice,
  useWarehouseFacilities,
  useWarehouseIdempotencyKey,
  useWarehouseInvoice,
  useWarehouseInvoices,
  useWarehousePeriodExpenseAttestations,
  useWarehouseExpensePeriodPreview,
} from '../../lib/warehouse';
import type {
  WarehouseExpenseAttestation,
  WarehouseExpensePeriodPreview,
  WarehouseInvoiceDetailResponse,
  WarehouseInvoiceStatus,
  WarehouseInvoiceSummary,
} from '@sourcito/schemas';
import {
  IncomeStatement,
  type StatementMarkers,
  type StatementRowKey,
} from './finance/IncomeStatement';
import { NeedsYou, type NeedsYouItem } from './finance/NeedsYou';

type PayablesController = ReturnType<typeof usePayablesController>;

function usePayablesController(enabled: boolean) {
  const facilitiesQuery = useWarehouseFacilities(enabled);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const facilities = facilitiesQuery.data ?? [];
  const selectedFacility = resolveWarehouseFacility(facilities, selectedFacilityId);
  const facilityId = selectedFacility?.id ?? null;
  const invoicesQuery = useWarehouseInvoices(
    { warehouseId: facilityId },
    enabled && Boolean(facilityId)
  );
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const invoiceQuery = useWarehouseInvoice(
    facilityId,
    selectedInvoiceId ?? undefined,
    enabled && Boolean(facilityId && selectedInvoiceId)
  );
  const transition = useTransitionWarehouseInvoice();
  const credit = useCreateWarehouseCreditNote();
  const payment = useRecordWarehouseInvoicePayment();
  const reverse = useReverseWarehouseInvoicePayment();

  useEffect(() => {
    if (facilities.length === 1) {
      setSelectedFacilityId(facilities[0]?.id ?? null);
    } else if (
      facilities.length === 0 ||
      !facilities.some((item) => item.id === selectedFacilityId)
    ) {
      setSelectedFacilityId(null);
    }
  }, [facilities, selectedFacilityId]);

  useEffect(() => {
    setSelectedInvoiceId(null);
  }, [facilityId]);

  useEffect(() => {
    const rows = invoicesQuery.data ?? [];
    if (selectedInvoiceId && !rows.some((row) => row.invoice.id === selectedInvoiceId)) {
      setSelectedInvoiceId(null);
    }
  }, [invoicesQuery.data, selectedInvoiceId]);

  return {
    facilitiesQuery,
    facilities,
    selectedFacility,
    selectedFacilityId: facilityId,
    setSelectedFacilityId,
    invoicesQuery,
    selectedInvoiceId,
    setSelectedInvoiceId,
    invoiceQuery,
    transition,
    credit,
    payment,
    reverse,
  };
}

type PayablesCommand =
  | { kind: 'submit' | 'approve' | 'dispute' | 'void'; invoice: WarehouseInvoiceSummary['invoice'] }
  | { kind: 'payment'; invoice: WarehouseInvoiceSummary['invoice'] }
  | { kind: 'credit'; invoice: WarehouseInvoiceSummary['invoice'] }
  | {
      kind: 'reverse';
      invoice: WarehouseInvoiceSummary['invoice'];
      paymentId: string;
    };

type PayablesFormValues = {
  reason?: string;
  amount?: string;
  paidAt?: string;
  reference?: string;
  creditIssueDate?: string;
  creditStart?: string;
  creditEnd?: string;
  creditTax?: string;
  invoiceLineIds?: string[];
};

type ConfirmedPayablesRequest = {
  commandKind: PayablesCommand['kind'];
  invoiceId: string;
  formFingerprint: string;
  semanticInput: Record<string, unknown>;
  variables: unknown;
};

function refetchFailed(result: PromiseSettledResult<unknown>): boolean {
  if (result.status === 'rejected') return true;
  return Boolean(
    result.value &&
    typeof result.value === 'object' &&
    (result.value as { isError?: boolean }).isError
  );
}

function payablesFormFingerprint(input: Record<string, unknown>): string {
  const { expectedVersion: _expectedVersion, ...formInput } = input;
  return JSON.stringify(formInput);
}

/** The semantic, action-specific payload used to retain/rotate a command key. */
export function buildPayablesIdempotencyInput(
  command: PayablesCommand,
  invoice: WarehouseInvoiceSummary['invoice'],
  values: PayablesFormValues = {}
): Record<string, unknown> {
  const base = {
    invoiceId: invoice.id,
    warehouseId: invoice.warehouseId,
    expectedVersion: invoice.version,
    action: command.kind,
  };
  if (
    command.kind === 'submit' ||
    command.kind === 'approve' ||
    command.kind === 'dispute' ||
    command.kind === 'void'
  ) {
    const needsReason =
      command.kind === 'dispute' ||
      command.kind === 'void' ||
      (command.kind === 'approve' && invoice.status === 'disputed');
    return needsReason ? { ...base, reason: values.reason?.trim() ?? '' } : base;
  }
  if (command.kind === 'payment') {
    return {
      ...base,
      amountCents: parseExactCents(values.amount ?? '', false),
      paidAt: normalizePaidAt(values.paidAt),
      reference: values.reference?.trim() || null,
    };
  }
  if (command.kind === 'credit') {
    return {
      ...base,
      invoiceLineIds: [...(values.invoiceLineIds ?? [])].sort(),
      issueDate: normalizeDateInput(values.creditIssueDate),
      servicePeriodStart: normalizeDateInput(values.creditStart),
      servicePeriodEnd: normalizeDateInput(values.creditEnd),
      taxCents: parseExactCents(values.creditTax ?? '', true),
      reason: values.reason?.trim() ?? '',
    };
  }
  if (command.kind === 'reverse') {
    return { ...base, paymentId: command.paymentId, reason: values.reason?.trim() ?? '' };
  }
  return base;
}

type PayablesField =
  | 'amount'
  | 'paidAt'
  | 'reference'
  | 'reason'
  | 'creditIssueDate'
  | 'creditStart'
  | 'creditEnd'
  | 'creditTax'
  | 'invoiceLineIds';
type PayablesValidation = Partial<Record<PayablesField, string>>;

function PayablesWorkspace({
  enabled,
  controller,
}: {
  enabled: boolean;
  controller: PayablesController;
}) {
  const {
    facilitiesQuery,
    facilities,
    selectedFacility,
    selectedFacilityId,
    setSelectedFacilityId,
    invoicesQuery,
    selectedInvoiceId,
    setSelectedInvoiceId,
    invoiceQuery,
    transition,
    credit,
    payment,
    reverse,
  } = controller;
  const [command, setCommand] = useState<PayablesCommand | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [reference, setReference] = useState('');
  const [creditIssueDate, setCreditIssueDate] = useState('');
  const [creditStart, setCreditStart] = useState('');
  const [creditEnd, setCreditEnd] = useState('');
  const [creditTax, setCreditTax] = useState('0.00');
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [validation, setValidation] = useState<PayablesValidation>({});
  // Faraz screenshot review (2026-09-02): "Warehouse accounts payable
  // ready." was a pure status announcement with nothing to say — this
  // region is reused for real transition messages below (invoice changed,
  // command pending/failed), so it starts empty rather than removed.
  const [announcement, setAnnouncement] = useState('');
  const [alert, setAlert] = useState('');
  const [stale, setStale] = useState(false);
  const [staleRefreshing, setStaleRefreshing] = useState(false);
  const [staleRefreshFailed, setStaleRefreshFailed] = useState(false);
  const [staleReadyVersion, setStaleReadyVersion] = useState<number | null>(null);
  const [staleBaseVersion, setStaleBaseVersion] = useState<number | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconciliationFailed, setReconciliationFailed] = useState(false);
  const [confirmedRequest, setConfirmedRequest] = useState<ConfirmedPayablesRequest | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const inFlightRef = useRef(false);
  const ownedRefreshRef = useRef<'requesting' | 'reconciling' | null>(null);
  const reconciliationMessageRef = useRef('Warehouse payables command succeeded.');
  const reconciliationTargetRef = useRef<string | null>(null);
  const detailHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const [focusInvoiceId, setFocusInvoiceId] = useState<string | null>(null);
  const commandDetailFingerprintRef = useRef<string | null>(null);
  const previousSelectionRef = useRef({
    facilityId: selectedFacilityId,
    invoiceId: selectedInvoiceId,
  });
  const isPending =
    requesting ||
    reconciling ||
    staleRefreshing ||
    transition.isPending ||
    credit.isPending ||
    payment.isPending ||
    reverse.isPending;
  const detailRefreshing = Boolean(invoiceQuery.isFetching);
  const blocked =
    isPending ||
    inFlightRef.current ||
    stale ||
    facilitiesQuery.isLoading ||
    facilitiesQuery.isFetching ||
    facilitiesQuery.isError ||
    invoicesQuery.isLoading ||
    invoicesQuery.isFetching ||
    invoicesQuery.isError ||
    invoiceQuery.isLoading ||
    invoiceQuery.isFetching ||
    invoiceQuery.isError ||
    detailRefreshing;
  const selectedDetail = invoiceQuery.data;
  const resumeUnavailable = Boolean(
    invoiceQuery.isLoading || invoiceQuery.isFetching || invoiceQuery.isError || !selectedDetail
  );
  const canReviewStale = Boolean(
    stale &&
    command &&
    staleReadyVersion !== null &&
    staleReadyVersion !== staleBaseVersion &&
    selectedDetail?.invoice.id === command.invoice.id &&
    selectedDetail.invoice.version === staleReadyVersion &&
    !invoiceQuery.isLoading &&
    !invoiceQuery.isFetching &&
    !invoiceQuery.isError &&
    commandStillAllowed(command, selectedDetail)
  );
  const currentInvoice = command
    ? selectedDetail?.invoice.id === command.invoice.id
      ? selectedDetail.invoice
      : command.invoice
    : selectedDetail?.invoice;
  const currentCommandPayload =
    command && currentInvoice
      ? buildPayablesIdempotencyInput(command, currentInvoice, {
          reason,
          amount,
          reference,
          paidAt,
          creditIssueDate,
          creditStart,
          creditEnd,
          creditTax,
          invoiceLineIds: selectedLineIds,
        })
      : null;
  const currentFormFingerprint = currentCommandPayload
    ? payablesFormFingerprint(currentCommandPayload)
    : null;
  const reusableConfirmedRequest =
    command &&
    confirmedRequest &&
    confirmedRequest.commandKind === command.kind &&
    confirmedRequest.invoiceId === command.invoice.id &&
    currentFormFingerprint === confirmedRequest.formFingerprint
      ? confirmedRequest
      : null;
  const commandPayload = reusableConfirmedRequest?.semanticInput ?? currentCommandPayload;
  const idempotencyKey = useWarehouseIdempotencyKey(commandPayload);

  function clearCommandState() {
    commandDetailFingerprintRef.current = null;
    setCommand(null);
    setDialogOpen(false);
    setStale(false);
    setStaleRefreshing(false);
    setStaleRefreshFailed(false);
    setStaleReadyVersion(null);
    setStaleBaseVersion(null);
    setReason('');
    setAmount('');
    setPaidAt(new Date().toISOString().slice(0, 16));
    setReference('');
    setCreditIssueDate('');
    setCreditStart('');
    setCreditEnd('');
    setCreditTax('0.00');
    setSelectedLineIds([]);
    setValidation({});
    setConfirmedRequest(null);
  }

  function showValidation(next: PayablesValidation) {
    setValidation(next);
    setAnnouncement('Validation is needed before this command can be submitted.');
  }

  function beginRequest() {
    inFlightRef.current = true;
    ownedRefreshRef.current = 'requesting';
    setRequesting(true);
    setAnnouncement('Warehouse payables command pending…');
  }

  function selectFacility(id: string | null) {
    if (isPending || inFlightRef.current) return;
    clearCommandState();
    setSelectedFacilityId(id);
    setSelectedInvoiceId(null);
  }

  function selectInvoice(id: string) {
    if (isPending || inFlightRef.current) return;
    clearCommandState();
    setSelectedInvoiceId(id);
  }

  function resumeCommand() {
    if (!command) return;
    if (resumeUnavailable) return;
    const detailInvoice = selectedDetail?.invoice;
    const sameContext =
      selectedFacilityId === command.invoice.warehouseId &&
      detailInvoice?.id === command.invoice.id &&
      commandStillAllowed(command, selectedDetail);
    if (!sameContext) {
      clearCommandState();
      setAnnouncement('The retained command is no longer valid for the selected invoice.');
      return;
    }
    setAlert('');
    setDialogOpen(true);
  }

  useEffect(() => {
    const previous = previousSelectionRef.current;
    if (
      command &&
      (previous.facilityId !== selectedFacilityId || previous.invoiceId !== selectedInvoiceId)
    ) {
      clearCommandState();
    }
    previousSelectionRef.current = { facilityId: selectedFacilityId, invoiceId: selectedInvoiceId };
  }, [command, selectedFacilityId, selectedInvoiceId]);

  useEffect(() => {
    if (
      !focusInvoiceId ||
      !selectedDetail ||
      selectedDetail.invoice.id !== focusInvoiceId ||
      invoiceQuery.isLoading ||
      invoiceQuery.isFetching ||
      invoiceQuery.isError
    ) {
      return;
    }
    const focus = () => {
      detailHeadingRef.current?.focus?.();
      setFocusInvoiceId(null);
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(focus);
    else focus();
  }, [
    focusInvoiceId,
    invoiceQuery.isError,
    invoiceQuery.isFetching,
    invoiceQuery.isLoading,
    selectedDetail,
  ]);

  useEffect(() => {
    if (!dialogOpen) triggerRef.current?.focus?.();
  }, [dialogOpen]);

  function openCommand(next: PayablesCommand, trigger?: HTMLButtonElement) {
    if (!commandStillAllowed(next, selectedDetail)) {
      setAnnouncement('The invoice changed; this action is no longer available.');
      return;
    }
    triggerRef.current = trigger ?? null;
    setCommand(next);
    setDialogOpen(true);
    setReason('');
    setValidation({});
    setAlert('');
    setConfirmedRequest(null);
    if (next.kind === 'payment') {
      setAmount('');
      setReference('');
      setPaidAt(new Date().toISOString().slice(0, 16));
    }
    if (next.kind === 'credit') {
      const invoice = next.invoice;
      setCreditIssueDate(invoice.issueDate.slice(0, 10));
      setCreditStart(invoice.servicePeriodStart.slice(0, 10));
      setCreditEnd(invoice.servicePeriodEnd.slice(0, 10));
      setCreditTax('0.00');
      setSelectedLineIds([]);
    }
  }

  useEffect(() => {
    if (!command || !selectedDetail || selectedDetail.invoice.id !== command.invoice.id) {
      commandDetailFingerprintRef.current = null;
      return;
    }
    const fingerprint = JSON.stringify({
      id: selectedDetail.invoice.id,
      version: selectedDetail.invoice.version,
      status: selectedDetail.invoice.status,
      documentType: selectedDetail.invoice.documentType,
      payments: selectedDetail.payments.map((payment) => [payment.id, payment.reversedAt]),
    });
    const previousFingerprint = commandDetailFingerprintRef.current;
    commandDetailFingerprintRef.current = fingerprint;
    if (ownedRefreshRef.current) return;
    if (!dialogOpen || !previousFingerprint || previousFingerprint === fingerprint) return;
    if (!commandStillAllowed(command, selectedDetail)) {
      clearCommandState();
      setAnnouncement('The invoice changed; this action is no longer available.');
      return;
    }
    setDialogOpen(false);
    setStale(true);
    setStaleBaseVersion(command.invoice.version);
    setStaleReadyVersion(selectedDetail.invoice.version);
    setStaleRefreshFailed(false);
    setAnnouncement('The invoice changed. Review the latest version before retrying.');
  }, [command, dialogOpen, selectedDetail]);

  function closeCommand() {
    if (isPending || inFlightRef.current) return;
    clearCommandState();
  }

  function staleError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    const candidate = error as Error & { status?: number; details?: { error?: { code?: string } } };
    return (
      candidate.status === 409 &&
      (candidate.details?.error?.code === 'STALE_INVOICE' ||
        /stale|version/i.test(candidate.message))
    );
  }

  function confirmedVariables<T>(variables: T): T {
    if (reusableConfirmedRequest) return reusableConfirmedRequest.variables as T;
    if (command && currentCommandPayload && currentFormFingerprint) {
      setConfirmedRequest({
        commandKind: command.kind,
        invoiceId: command.invoice.id,
        formFingerprint: currentFormFingerprint,
        semanticInput: currentCommandPayload,
        variables,
      });
    }
    return variables;
  }

  async function reconcileCommittedReads(message: string, preferredTargetId: string | null) {
    ownedRefreshRef.current = 'reconciling';
    setReconciling(true);
    setReconciliationFailed(false);
    const [detailResult, listResult] = await Promise.allSettled([
      invoiceQuery.refetch(),
      invoicesQuery.refetch(),
    ]);
    const detailFailed = refetchFailed(detailResult);
    const listFailed = refetchFailed(listResult);
    const refreshed =
      detailResult.status === 'fulfilled' && !detailFailed
        ? (detailResult.value as { data?: WarehouseInvoiceDetailResponse } | undefined)
        : undefined;
    const next = refreshed?.data;
    const targetId = preferredTargetId ?? next?.invoice.id ?? reconciliationTargetRef.current;
    if (targetId && selectedInvoiceId !== targetId) setSelectedInvoiceId(targetId);
    setFocusInvoiceId(targetId);
    const failed = detailFailed || listFailed;
    setReconciliationFailed(failed);
    setAnnouncement(
      failed
        ? `${message} Some invoice data could not be refreshed; retry reconciliation.`
        : message
    );
    ownedRefreshRef.current = null;
    setReconciling(false);
  }

  async function refreshAfterCommittedMutation(message: string, targetId: string | null) {
    reconciliationMessageRef.current = message;
    reconciliationTargetRef.current = targetId;
    setAlert('');
    clearCommandState();
    await reconcileCommittedReads(message, targetId);
  }

  async function retryReconciliation() {
    await reconcileCommittedReads(
      reconciliationMessageRef.current,
      reconciliationTargetRef.current
    );
  }

  async function refreshStaleReads(targetId: string, baseVersion: number) {
    ownedRefreshRef.current = 'reconciling';
    setStaleRefreshing(true);
    setStaleRefreshFailed(false);
    setStaleReadyVersion(null);
    const [detailResult, listResult] = await Promise.allSettled([
      invoiceQuery.refetch(),
      invoicesQuery.refetch(),
    ]);
    const detailFailed = refetchFailed(detailResult);
    const listFailed = refetchFailed(listResult);
    const refreshed =
      detailResult.status === 'fulfilled' && !detailFailed
        ? (detailResult.value as { data?: WarehouseInvoiceDetailResponse } | undefined)
        : undefined;
    const next = refreshed?.data;
    const detailReady = Boolean(
      next?.invoice.id === targetId &&
      next.invoice.version !== baseVersion &&
      command &&
      commandStillAllowed(command, next)
    );
    setStaleReadyVersion(detailReady ? (next?.invoice.version ?? null) : null);
    const failed = detailFailed || listFailed || !detailReady;
    setStaleRefreshFailed(failed);
    setAnnouncement(
      failed
        ? 'Latest invoice data could not be refreshed; retry the invoice refresh.'
        : 'Latest invoice version must be reviewed before retrying.'
    );
    ownedRefreshRef.current = null;
    setStaleRefreshing(false);
  }

  function reviewLatestVersion() {
    if (!canReviewStale) return;
    setConfirmedRequest(null);
    setStale(false);
    setStaleRefreshFailed(false);
    setStaleReadyVersion(null);
    setStaleBaseVersion(null);
  }

  async function confirmCommand() {
    if (!command || !currentInvoice || blocked || inFlightRef.current) return;
    if (!commandStillAllowed(command, selectedDetail)) {
      clearCommandState();
      setAnnouncement('The invoice changed; this action is no longer available.');
      return;
    }
    setValidation({});
    setAlert('');
    try {
      if (
        command.kind === 'submit' ||
        command.kind === 'approve' ||
        command.kind === 'dispute' ||
        command.kind === 'void'
      ) {
        if (
          (command.kind === 'dispute' ||
            command.kind === 'void' ||
            (command.kind === 'approve' && currentInvoice.status === 'disputed')) &&
          !reason.trim()
        ) {
          showValidation({ reason: 'A nonblank reason is required for this action.' });
          return;
        }
        const needsReason =
          command.kind === 'dispute' ||
          command.kind === 'void' ||
          (command.kind === 'approve' && currentInvoice.status === 'disputed');
        const variables = confirmedVariables({
          invoiceId: currentInvoice.id,
          warehouseId: currentInvoice.warehouseId,
          action: command.kind,
          ...(needsReason ? { reason: reason.trim() } : {}),
          expectedVersion: currentInvoice.version,
          idempotencyKey,
        });
        beginRequest();
        await transition.mutateAsync(variables);
        await refreshAfterCommittedMutation(
          `${invoiceDocumentLabel(currentInvoice)} ${command.kind} succeeded.`,
          currentInvoice.id
        );
      } else if (command.kind === 'payment') {
        const amountCents = parseExactCents(amount, false);
        if (amountCents === null || amountCents <= 0) {
          showValidation({ amount: 'Enter a positive whole-cent payment amount.' });
          return;
        }
        if (!paidAt) {
          showValidation({ paidAt: 'Paid at is required.' });
          return;
        }
        let normalizedPaidAt: string;
        try {
          normalizedPaidAt = toUtcIso(paidAt);
        } catch {
          showValidation({ paidAt: 'Paid at must be a valid UTC date and time.' });
          return;
        }
        const variables = confirmedVariables({
          invoiceId: currentInvoice.id,
          warehouseId: currentInvoice.warehouseId,
          amountCents,
          paidAt: normalizedPaidAt,
          reference: reference.trim() ? reference.trim() : null,
          expectedVersion: currentInvoice.version,
          idempotencyKey,
        });
        beginRequest();
        const response = await payment.mutateAsync(variables);
        await refreshAfterCommittedMutation(
          'Payment recorded.',
          response?.invoice?.invoice?.id ?? currentInvoice.id
        );
      } else if (command.kind === 'reverse') {
        if (!reason.trim()) {
          showValidation({ reason: 'A nonblank reversal reason is required.' });
          return;
        }
        const variables = confirmedVariables({
          invoiceId: currentInvoice.id,
          paymentId: command.paymentId,
          warehouseId: currentInvoice.warehouseId,
          reason: reason.trim(),
          expectedVersion: currentInvoice.version,
          idempotencyKey,
        });
        beginRequest();
        await reverse.mutateAsync(variables);
        await refreshAfterCommittedMutation('Payment reversed.', currentInvoice.id);
      } else {
        const taxCents = parseExactCents(creditTax, true);
        if (selectedLineIds.length < 1) {
          showValidation({ invoiceLineIds: 'Select at least one original invoice line.' });
          return;
        }
        if (taxCents === null || taxCents > 0) {
          showValidation({ creditTax: 'Credit tax must be zero or negative whole cents.' });
          return;
        }
        const issueDate = normalizeDateInput(creditIssueDate);
        const serviceStart = normalizeDateInput(creditStart);
        const serviceEnd = normalizeDateInput(creditEnd);
        if (!issueDate || !serviceStart || !serviceEnd) {
          showValidation({
            ...(issueDate ? {} : { creditIssueDate: 'Enter a valid issue date.' }),
            ...(serviceStart ? {} : { creditStart: 'Enter a valid service period start.' }),
            ...(serviceEnd ? {} : { creditEnd: 'Enter a valid service period end.' }),
          });
          return;
        }
        if (new Date(serviceStart) > new Date(serviceEnd)) {
          showValidation({ creditEnd: 'Service period end must be on or after start.' });
          return;
        }
        if (!reason.trim()) {
          showValidation({ reason: 'A nonblank credit reason is required.' });
          return;
        }
        const variables = confirmedVariables({
          invoiceId: currentInvoice.id,
          warehouseId: currentInvoice.warehouseId,
          invoiceLineIds: [...selectedLineIds].sort(),
          issueDate,
          servicePeriodStart: serviceStart,
          servicePeriodEnd: serviceEnd,
          taxCents,
          reason: reason.trim(),
          expectedVersion: currentInvoice.version,
          idempotencyKey,
        });
        beginRequest();
        const created = await credit.mutateAsync(variables);
        await refreshAfterCommittedMutation('Credit note created.', created.invoice.id);
      }
    } catch (error) {
      if (staleError(error)) {
        setStale(true);
        setStaleBaseVersion(currentInvoice.version);
        setDialogOpen(false);
        await refreshStaleReads(currentInvoice.id, currentInvoice.version);
      } else {
        ownedRefreshRef.current = null;
        const message = payablesErrorMessage(error);
        setAlert(message);
        setAnnouncement('Warehouse payables command failed. Review the error before retrying.');
        const code = payablesErrorCode(error);
        if (code === 'CREDIT_TAX_EXCEEDED') setValidation({ creditTax: message });
        if (code === 'INVALID_CREDIT_SOURCE') setValidation({ invoiceLineIds: message });
      }
      setDialogOpen(false);
    } finally {
      inFlightRef.current = false;
      if (ownedRefreshRef.current === 'requesting') ownedRefreshRef.current = null;
      setRequesting(false);
    }
  }

  if (!enabled) {
    return (
      <section aria-labelledby="warehouse-accounts-payable-heading">
        <h2 id="warehouse-accounts-payable-heading" className="text-xl font-medium">
          Warehouse bills
        </h2>
        <PayablesLiveRegions announcement="Warehouse payables unavailable while the module is off." />
        <Card>
          <p className="text-sm text-fg-secondary mt-3">
            Warehouse payables are unavailable while the Warehouse module is off.
          </p>
        </Card>
      </section>
    );
  }
  if (facilitiesQuery.isLoading)
    return (
      <section aria-labelledby="warehouse-accounts-payable-heading" className="space-y-4">
        <h2 id="warehouse-accounts-payable-heading" className="text-xl font-medium">
          Warehouse bills
        </h2>
        <PayablesLiveRegions announcement="Loading warehouse facilities…" />
        <div className="max-w-sm">
          <Skeleton className="h-10 w-full rounded-sm" />
        </div>
        <div className="space-y-3">
          <PayablesInvoiceCardSkeleton />
          <PayablesInvoiceCardSkeleton />
        </div>
      </section>
    );
  if (facilitiesQuery.isError)
    return (
      <PayablesState
        title="Could not load warehouse facilities"
        retry={facilitiesQuery.refetch}
        heading="warehouse-accounts-payable-heading"
      />
    );
  if (facilities.length === 0)
    return (
      <PayablesState
        title="No warehouse facilities"
        detail="Add a facility before reviewing payables."
        heading="warehouse-accounts-payable-heading"
      />
    );
  if (!selectedFacility)
    return (
      <section aria-labelledby="warehouse-accounts-payable-heading" className="space-y-4">
        <h2 id="warehouse-accounts-payable-heading" className="text-xl font-medium">
          Warehouse bills
        </h2>
        <PayablesLiveRegions announcement="Select a facility to load warehouse payables." />
        <PayablesFacilitySelect
          facilities={facilities}
          selected={selectedFacilityId}
          onChange={selectFacility}
        />
      </section>
    );

  const rows = invoicesQuery.data ?? [];
  const currentRows = rows;
  return (
    <section aria-labelledby="warehouse-accounts-payable-heading" className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">WAREHOUSE</p>
        <h2
          id="warehouse-accounts-payable-heading"
          className="text-xl sm:text-2xl font-medium"
          tabIndex={-1}
          ref={detailHeadingRef}
        >
          Warehouse bills
        </h2>
        <p className="text-sm text-fg-secondary mt-2">
          Approve a bill to count it in your costs. Record payments here.
        </p>
      </div>
      {facilities.length > 1 && (
        <PayablesFacilitySelect
          facilities={facilities}
          selected={selectedFacilityId}
          onChange={selectFacility}
          disabled={isPending}
        />
      )}
      <PayablesLiveRegions announcement={announcement} alert={alert} />
      {reconciliationFailed && (
        <Card>
          <p className="text-sm text-fg-secondary">
            The command succeeded, but the latest invoice reads did not fully reconcile.
          </p>
          <Button
            className="mt-3"
            size="sm"
            onClick={() => void retryReconciliation()}
            disabled={isPending}
          >
            Retry reconciliation
          </Button>
        </Card>
      )}
      {stale && (
        <Card>
          <p className="text-sm text-fg-secondary">
            The invoice changed on the server. Review the latest version before retrying.
          </p>
          <Button
            className="mt-3"
            size="sm"
            onClick={reviewLatestVersion}
            disabled={!canReviewStale}
          >
            Review latest version
          </Button>
          {staleRefreshFailed && (
            <Button
              className="mt-3"
              size="sm"
              onClick={() =>
                command &&
                void refreshStaleReads(
                  command.invoice.id,
                  staleBaseVersion ?? command.invoice.version
                )
              }
              disabled={staleRefreshing}
            >
              Retry invoice refresh
            </Button>
          )}
        </Card>
      )}
      {command && !dialogOpen && !stale && (
        <Card>
          <p className="text-sm text-fg-secondary">
            The command is retained so you can correct it and resume.
          </p>
          <Button className="mt-3" size="sm" onClick={resumeCommand} disabled={resumeUnavailable}>
            Resume {command.kind}
          </Button>
        </Card>
      )}
      {invoicesQuery.isLoading ? (
        <div className="space-y-3" aria-label="Warehouse invoice list">
          <PayablesInvoiceCardSkeleton />
          <PayablesInvoiceCardSkeleton />
          <PayablesInvoiceCardSkeleton />
        </div>
      ) : invoicesQuery.isError ? (
        <PayablesErrorCard title="Could not load invoice history" retry={invoicesQuery.refetch} />
      ) : currentRows.length === 0 ? (
        <Card>
          <p className="text-sm text-fg-secondary">No warehouse invoices or credit notes yet.</p>
        </Card>
      ) : (
        <div
          data-invoice-layout="responsive"
          className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]"
        >
          <div className="space-y-3" aria-label="Warehouse invoice list">
            {currentRows.map((row) => (
              <PayablesInvoiceCard
                key={row.invoice.id}
                row={row}
                facilityName={selectedFacility.name}
                selected={row.invoice.id === selectedInvoiceId}
                disabled={isPending}
                onSelect={() => selectInvoice(row.invoice.id)}
              />
            ))}
          </div>
          <div className="min-w-0">
            {invoiceQuery.isLoading ? (
              <PayablesDetailSkeleton />
            ) : invoiceQuery.isError ? (
              <PayablesErrorCard
                title="Could not load invoice details"
                retry={invoiceQuery.refetch}
              />
            ) : selectedDetail ? (
              <PayablesDetail
                detail={selectedDetail}
                facilityName={selectedFacility.name}
                blocked={blocked}
                headingRef={detailHeadingRef}
                onAction={openCommand}
              />
            ) : (
              <Card>
                <p className="text-sm text-fg-secondary">
                  Select a bill to see its lines and actions.
                </p>
              </Card>
            )}
          </div>
        </div>
      )}
      <PayablesCommandDialog
        command={command}
        reason={reason}
        setReason={setReason}
        amount={amount}
        setAmount={setAmount}
        paidAt={paidAt}
        setPaidAt={setPaidAt}
        reference={reference}
        setReference={setReference}
        creditIssueDate={creditIssueDate}
        setCreditIssueDate={setCreditIssueDate}
        creditStart={creditStart}
        setCreditStart={setCreditStart}
        creditEnd={creditEnd}
        setCreditEnd={setCreditEnd}
        creditTax={creditTax}
        setCreditTax={setCreditTax}
        selectedLineIds={selectedLineIds}
        setSelectedLineIds={setSelectedLineIds}
        detail={selectedDetail}
        validation={validation}
        onCancel={closeCommand}
        onConfirm={() => void confirmCommand()}
        pending={blocked}
        dismissDisabled={isPending}
        open={dialogOpen}
      />
    </section>
  );
}

function PayablesState({
  title,
  detail,
  retry,
  heading,
}: {
  title: string;
  detail?: string;
  retry?: (() => unknown) | undefined;
  heading?: string;
}) {
  return (
    <section aria-labelledby={heading} className="space-y-3">
      <h2 id={heading} className="text-xl font-medium">
        Warehouse bills
      </h2>
      <PayablesLiveRegions announcement={title} />
      <Card>
        <p className="text-sm text-fg-secondary">{title}</p>
        {detail && <p className="text-xs text-fg-tertiary mt-1">{detail}</p>}
        {retry && (
          <Button variant="ghost" size="sm" className="mt-3" onClick={() => void retry()}>
            Retry
          </Button>
        )}
      </Card>
    </section>
  );
}

function PayablesErrorCard({ title, retry }: { title: string; retry: () => unknown }) {
  return (
    <Card>
      <p className="text-sm text-fg-secondary">{title}</p>
      <Button variant="ghost" size="sm" className="mt-3" onClick={() => void retry()}>
        Retry
      </Button>
    </Card>
  );
}

function PayablesLiveRegions({
  announcement,
  alert = '',
}: {
  announcement: string;
  alert?: string;
}) {
  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="min-h-5 text-sm text-fg-secondary"
      >
        {announcement}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="min-h-5 text-sm text-negative"
      >
        {alert}
      </div>
    </>
  );
}

function PayablesFacilitySelect({
  facilities,
  selected,
  onChange,
  disabled = false,
}: {
  facilities: Array<{ id: string; name: string }>;
  selected: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}) {
  return (
    <div className="max-w-sm">
      <label htmlFor="finance-warehouse-facility" className="block text-sm font-medium mb-2">
        Facility
      </label>
      <select
        id="finance-warehouse-facility"
        value={selected ?? ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value || null)}
        className="w-full h-10 px-3 bg-surface border border-border-default rounded-sm text-sm"
      >
        <option value="">Select a facility</option>
        {facilities.map((facility) => (
          <option key={facility.id} value={facility.id}>
            {facility.name}
          </option>
        ))}
      </select>
      {facilities.length > 1 && !selected && (
        <p className="text-xs text-fg-secondary mt-2">Select a facility to load its payables.</p>
      )}
    </div>
  );
}

function invoiceDocumentLabel(invoice: { documentType: 'standard' | 'credit_note' }) {
  return invoice.documentType === 'credit_note' ? 'Credit note' : 'Invoice';
}

/** "May 1 – May 31, 2026" — full end date, start date without its
 * redundant year (R2 item 5, 2026-09-02 cold-viewer review: dates in the
 * payables card/detail were raw ISO). */
function financeDateRangeCopy(startIso: string, endIso: string): string {
  const end = formatFinanceDate(endIso);
  const start = formatFinanceDate(startIso).replace(/, \d{4}$/, '');
  return `${start} – ${end}`;
}

/** "May 2026" when the service period is one calendar month, else the ISO range. */
function invoiceServicePeriodLabel(invoice: {
  servicePeriodStart: string;
  servicePeriodEnd: string;
}): string {
  const start = invoice.servicePeriodStart.slice(0, 10);
  const end = invoice.servicePeriodEnd.slice(0, 10);
  const [y = 0, m = 0] = start.split('-').map(Number);
  if (!y || !m) return `${start} – ${end}`;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (start.endsWith('-01') && end === `${start.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`) {
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  return `${start} – ${end}`;
}

function invoicePaymentStatusLabel(status: string): string {
  switch (status) {
    case 'unpaid':
      return 'Unpaid';
    case 'partial':
      return 'Partially paid';
    case 'paid':
      return 'Paid';
    case 'not_applicable':
      return 'Not applicable';
    default:
      return status;
  }
}

function PayablesInvoiceCard({
  row,
  facilityName,
  selected,
  disabled,
  onSelect,
}: {
  row: WarehouseInvoiceSummary;
  facilityName: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const invoice = row.invoice;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={`${invoiceDocumentLabel(invoice)} ${invoiceServicePeriodLabel(invoice)} at ${facilityName}, issued ${invoice.issueDate.slice(0, 10)}`}
      className={`w-full text-left rounded-lg border p-4 ${selected ? 'border-accent bg-surface-elev' : 'border-border-subtle bg-surface'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-fg-tertiary">
            {invoiceDocumentLabel(invoice)}
          </p>
          <p className="text-sm font-medium text-fg-primary mt-1">
            {invoiceServicePeriodLabel(invoice)} · {facilityName}
          </p>
          <p className="text-xs text-fg-secondary mt-1">
            Issued {formatFinanceDate(invoice.issueDate)} · Service period{' '}
            {financeDateRangeCopy(invoice.servicePeriodStart, invoice.servicePeriodEnd)}
          </p>
        </div>
        <Badge variant="neutral">{invoiceStatusLabel(invoice.status)}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
        <span>
          <span className="block text-xs text-fg-tertiary">Total</span>
          <span className="tabular-nums">
            {formatWarehouseCents(invoice.totalCents, invoice.currency)}
          </span>
        </span>
        <span>
          <span className="block text-xs text-fg-tertiary">Balance</span>
          <span className="tabular-nums">
            {formatWarehouseCents(row.balanceCents, invoice.currency)}
          </span>
        </span>
      </div>
      <p className="text-xs text-fg-secondary mt-3">
        Payment status: {invoicePaymentStatusLabel(row.paymentStatus)}
      </p>
    </button>
  );
}

/** Matches PayablesInvoiceCard's shape (label, title line, subtext, badge,
 *  two-metric row, status line) for the invoice-list loading states. */
function PayablesInvoiceCardSkeleton() {
  return (
    <div className="rounded-lg border border-border-subtle bg-surface p-4" aria-hidden="true">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <SkeletonLine className="h-2.5 w-20" />
          <SkeletonLine className="h-4 w-44" />
          <SkeletonLine className="h-3 w-56" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full shrink-0" />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <SkeletonLine className="h-4 w-16" />
        <SkeletonLine className="h-4 w-16" />
      </div>
      <SkeletonLine className="h-3 w-40 mt-3" />
    </div>
  );
}

function actionAllowed(
  invoice: { status: WarehouseInvoiceStatus; documentType: 'standard' | 'credit_note' },
  action: PayablesCommand['kind']
) {
  if (action === 'payment')
    return (
      invoice.documentType === 'standard' && ['approved', 'partially_paid'].includes(invoice.status)
    );
  if (action === 'credit')
    return (
      invoice.documentType === 'standard' &&
      ['approved', 'partially_paid', 'paid'].includes(invoice.status)
    );
  if (action === 'reverse') return false;
  if (action === 'submit') return invoice.status === 'draft';
  if (action === 'approve') return invoice.status === 'submitted' || invoice.status === 'disputed';
  if (action === 'dispute') return invoice.status === 'submitted';
  return ['draft', 'submitted', 'disputed'].includes(invoice.status);
}

function commandStillAllowed(
  command: PayablesCommand,
  detail: WarehouseInvoiceDetailResponse | undefined
): boolean {
  if (!detail) return false;
  if (command.kind === 'reverse') {
    return (
      detail.invoice.documentType === 'standard' &&
      detail.payments.some((payment) => payment.id === command.paymentId && !payment.reversedAt)
    );
  }
  return actionAllowed(detail.invoice, command.kind);
}

function PayablesDetail({
  detail,
  facilityName,
  blocked,
  headingRef,
  onAction,
}: {
  detail: WarehouseInvoiceDetailResponse;
  facilityName: string;
  blocked: boolean;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  onAction: (command: PayablesCommand, trigger?: HTMLButtonElement) => void;
}) {
  const invoice = detail.invoice;
  return (
    <Card>
      <h3 ref={headingRef} tabIndex={-1} className="text-lg font-medium text-fg-primary">
        {invoiceDocumentLabel(invoice)} · {facilityName}
      </h3>
      <p className="text-sm text-fg-secondary mt-1">
        Issue date {formatFinanceDate(invoice.issueDate)} · Service period{' '}
        {financeDateRangeCopy(invoice.servicePeriodStart, invoice.servicePeriodEnd)}
      </p>
      <p className="text-xs text-fg-tertiary mt-1">
        Status: {invoiceStatusLabel(invoice.status)} · Payment status: {detail.paymentStatus} ·
        Version {invoice.version}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5">
        <Metric label="Total" value={formatWarehouseCents(invoice.totalCents, invoice.currency)} />
        <Metric label="Tax" value={formatWarehouseCents(invoice.taxCents, invoice.currency)} />
        <Metric label="Paid" value={formatWarehouseCents(detail.paidCents, invoice.currency)} />
        <Metric
          label="Balance"
          value={formatWarehouseCents(detail.balanceCents, invoice.currency)}
        />
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        {(['submit', 'approve', 'dispute', 'void', 'payment', 'credit'] as const)
          .filter((kind) => actionAllowed(invoice, kind))
          .map((kind) => (
            <Button
              key={kind}
              size="sm"
              variant={kind === 'void' ? 'destructive' : 'secondary'}
              disabled={blocked}
              onClick={(event) => onAction({ kind, invoice }, event?.currentTarget)}
            >
              {kind === 'payment'
                ? 'Record payment'
                : kind === 'credit'
                  ? 'Create credit note'
                  : kind === 'approve'
                    ? 'Approve'
                    : kind === 'dispute'
                      ? 'Dispute'
                      : kind === 'void'
                        ? 'Void'
                        : 'Submit'}
            </Button>
          ))}
      </div>
      <div className="mt-6 border-t border-border-subtle pt-4">
        <h4 className="text-sm font-medium">Frozen invoice lines</h4>
        <div className="divide-y divide-border-subtle mt-2">
          {detail.lines.map(({ line, allocations }) => (
            <div key={line.id} className="py-3 text-sm">
              <div className="flex justify-between gap-3">
                <span>
                  {line.code} · {line.description}
                </span>
                <span className="tabular-nums">
                  {formatWarehouseCents(line.netAmountCents, line.currency)}
                </span>
              </div>
              <p className="text-xs text-fg-tertiary mt-1">
                {line.accountingTreatment === 'capitalizable' ? 'Inventory cost' : 'Period expense'}{' '}
                · {allocations.length} allocation{allocations.length === 1 ? '' : 's'}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 border-t border-border-subtle pt-4">
        <h4 className="text-sm font-medium">Payment history</h4>
        {detail.payments.length === 0 ? (
          <p className="text-sm text-fg-tertiary mt-2">No payments recorded.</p>
        ) : (
          <div className="divide-y divide-border-subtle mt-2">
            {detail.payments.map((item) => (
              <div key={item.id} className="py-3 flex justify-between gap-3 text-sm">
                <span>
                  {item.paidAt.slice(0, 10)}
                  {item.reference ? ` · ${item.reference}` : ''}
                  {item.reversedAt ? ' · Reversed' : ''}
                </span>
                <span className="tabular-nums">
                  {formatWarehouseCents(item.amountCents, invoice.currency)}
                  {!item.reversedAt && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-2"
                      disabled={blocked}
                      onClick={(event) =>
                        onAction(
                          { kind: 'reverse', invoice, paymentId: item.id },
                          event?.currentTarget
                        )
                      }
                    >
                      Reverse
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

/** Matches PayablesDetail's shape (title, subtext, metric grid, frozen
 *  lines, payment history) for the invoice-detail loading state. */
function PayablesDetailSkeleton() {
  return (
    <Card>
      <div aria-hidden="true">
        <SkeletonLine className="h-5 w-48" />
        <SkeletonLine className="h-3.5 w-64 mt-2" />
        <SkeletonLine className="h-3 w-56 mt-1" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-1.5">
              <SkeletonLine className="h-2.5 w-10" />
              <SkeletonLine className="h-4 w-16" />
            </div>
          ))}
        </div>
        <div className="mt-6 border-t border-border-subtle pt-4">
          <SkeletonLine className="h-3.5 w-32" />
          <div className="divide-y divide-border-subtle mt-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="py-3">
                <div className="flex justify-between gap-3">
                  <SkeletonLine className="w-40" />
                  <SkeletonLine className="w-16" />
                </div>
                <SkeletonLine className="h-3 w-56 mt-2" />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 border-t border-border-subtle pt-4">
          <SkeletonLine className="h-3.5 w-32" />
          <div className="divide-y divide-border-subtle mt-2">
            {[0, 1].map((i) => (
              <div key={i} className="py-3 flex justify-between gap-3">
                <SkeletonLine className="w-32" />
                <SkeletonLine className="w-16" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

function PayablesCommandDialog({
  command,
  reason,
  setReason,
  amount,
  setAmount,
  paidAt,
  setPaidAt,
  reference,
  setReference,
  creditIssueDate,
  setCreditIssueDate,
  creditStart,
  setCreditStart,
  creditEnd,
  setCreditEnd,
  creditTax,
  setCreditTax,
  selectedLineIds,
  setSelectedLineIds,
  detail,
  validation,
  onCancel,
  onConfirm,
  pending,
  dismissDisabled,
  open,
}: {
  command: PayablesCommand | null;
  reason: string;
  setReason: (value: string) => void;
  amount: string;
  setAmount: (value: string) => void;
  paidAt: string;
  setPaidAt: (value: string) => void;
  reference: string;
  setReference: (value: string) => void;
  creditIssueDate: string;
  setCreditIssueDate: (value: string) => void;
  creditStart: string;
  setCreditStart: (value: string) => void;
  creditEnd: string;
  setCreditEnd: (value: string) => void;
  creditTax: string;
  setCreditTax: (value: string) => void;
  selectedLineIds: string[];
  setSelectedLineIds: (value: string[]) => void;
  detail?: WarehouseInvoiceDetailResponse;
  validation: PayablesValidation;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
  dismissDisabled: boolean;
  open: boolean;
}) {
  if (!command || !open) return null;
  const isCredit = command.kind === 'credit';
  const displayedInvoice = detail?.invoice ?? command.invoice;
  const fieldError = (field: PayablesField) => validation[field];
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !dismissDisabled && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Confirm{' '}
            {command.kind === 'payment'
              ? 'payment'
              : command.kind === 'credit'
                ? 'credit note'
                : command.kind}
          </DialogTitle>
          <DialogDescription>
            Confirm this warehouse accounts payable command for version {displayedInvoice.version}.
          </DialogDescription>
        </DialogHeader>
        {command.kind === 'payment' && (
          <div className="space-y-3">
            <label className="block text-sm">
              Amount ({displayedInvoice.currency})
              <Input
                id="finance-payment-amount"
                inputSize="sm"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                aria-invalid={Boolean(fieldError('amount'))}
                aria-describedby={fieldError('amount') ? 'finance-validation-amount' : undefined}
              />
              {fieldError('amount') && (
                <FieldError id="finance-validation-amount" message={fieldError('amount')} />
              )}
            </label>
            <label className="block text-sm">
              Paid at (UTC)
              <Input
                id="finance-payment-paid-at"
                inputSize="sm"
                type="datetime-local"
                value={paidAt}
                onChange={(event) => setPaidAt(event.target.value)}
                aria-invalid={Boolean(fieldError('paidAt'))}
                aria-describedby={fieldError('paidAt') ? 'finance-validation-paid-at' : undefined}
              />
              {fieldError('paidAt') && (
                <FieldError id="finance-validation-paid-at" message={fieldError('paidAt')} />
              )}
            </label>
            <label className="block text-sm">
              Reference (optional)
              <Input
                id="finance-payment-reference"
                inputSize="sm"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                aria-invalid={Boolean(fieldError('reference'))}
                aria-describedby={
                  fieldError('reference') ? 'finance-validation-reference' : undefined
                }
              />
              {fieldError('reference') && (
                <FieldError id="finance-validation-reference" message={fieldError('reference')} />
              )}
            </label>
          </div>
        )}
        {isCredit && detail && (
          <div className="space-y-3">
            <label className="block text-sm">
              Issue date
              <Input
                id="finance-credit-issue-date"
                inputSize="sm"
                type="date"
                value={creditIssueDate}
                onChange={(event) => setCreditIssueDate(event.target.value)}
                aria-invalid={Boolean(fieldError('creditIssueDate'))}
                aria-describedby={
                  fieldError('creditIssueDate') ? 'finance-validation-credit-issue-date' : undefined
                }
              />
              {fieldError('creditIssueDate') && (
                <FieldError
                  id="finance-validation-credit-issue-date"
                  message={fieldError('creditIssueDate')}
                />
              )}
            </label>
            <label className="block text-sm">
              Service period start
              <Input
                id="finance-credit-start"
                inputSize="sm"
                type="date"
                value={creditStart}
                onChange={(event) => setCreditStart(event.target.value)}
                aria-invalid={Boolean(fieldError('creditStart'))}
                aria-describedby={
                  fieldError('creditStart') ? 'finance-validation-credit-start' : undefined
                }
              />
              {fieldError('creditStart') && (
                <FieldError
                  id="finance-validation-credit-start"
                  message={fieldError('creditStart')}
                />
              )}
            </label>
            <label className="block text-sm">
              Service period end
              <Input
                id="finance-credit-end"
                inputSize="sm"
                type="date"
                value={creditEnd}
                onChange={(event) => setCreditEnd(event.target.value)}
                aria-invalid={Boolean(fieldError('creditEnd'))}
                aria-describedby={
                  fieldError('creditEnd') ? 'finance-validation-credit-end' : undefined
                }
              />
              {fieldError('creditEnd') && (
                <FieldError id="finance-validation-credit-end" message={fieldError('creditEnd')} />
              )}
            </label>
            <label className="block text-sm">
              Tax adjustment ({displayedInvoice.currency})
              <Input
                id="finance-credit-tax"
                inputSize="sm"
                value={creditTax}
                onChange={(event) => setCreditTax(event.target.value)}
                aria-invalid={Boolean(fieldError('creditTax'))}
                aria-describedby={
                  fieldError('creditTax') ? 'finance-validation-credit-tax' : undefined
                }
              />
              {fieldError('creditTax') && (
                <FieldError id="finance-validation-credit-tax" message={fieldError('creditTax')} />
              )}
            </label>
            <fieldset
              aria-invalid={Boolean(fieldError('invoiceLineIds'))}
              aria-describedby={
                fieldError('invoiceLineIds') ? 'finance-validation-invoice-lines' : undefined
              }
            >
              <legend className="text-sm">Original frozen invoice lines</legend>
              {detail.lines.map(({ line }) => (
                <label key={line.id} className="flex gap-2 text-sm mt-2">
                  <input
                    type="checkbox"
                    checked={selectedLineIds.includes(line.id)}
                    onChange={() =>
                      setSelectedLineIds(
                        selectedLineIds.includes(line.id)
                          ? selectedLineIds.filter((id) => id !== line.id)
                          : [...selectedLineIds, line.id]
                      )
                    }
                  />
                  {line.code} · {formatWarehouseCents(line.netAmountCents, line.currency)}
                </label>
              ))}
            </fieldset>
            {fieldError('invoiceLineIds') && (
              <FieldError
                id="finance-validation-invoice-lines"
                message={fieldError('invoiceLineIds')}
              />
            )}
          </div>
        )}
        {!isCredit &&
          (command.kind === 'dispute' ||
            command.kind === 'void' ||
            command.kind === 'reverse' ||
            (command.kind === 'approve' && displayedInvoice.status === 'disputed')) && (
            <label className="block text-sm">
              {command.kind === 'approve' && displayedInvoice.status === 'disputed'
                ? 'Resolution reason'
                : 'Reason'}
              <textarea
                id="finance-command-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                aria-invalid={Boolean(fieldError('reason'))}
                aria-describedby={fieldError('reason') ? 'finance-validation-reason' : undefined}
                className="w-full mt-1 min-h-20 border border-border-default rounded-sm p-2 bg-surface"
              />
              {fieldError('reason') && (
                <FieldError id="finance-validation-reason" message={fieldError('reason')} />
              )}
            </label>
          )}
        {isCredit && (
          <label className="block text-sm mt-3">
            Reason
            <textarea
              id="finance-credit-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              aria-invalid={Boolean(fieldError('reason'))}
              aria-describedby={fieldError('reason') ? 'finance-validation-reason' : undefined}
              className="w-full mt-1 min-h-20 border border-border-default rounded-sm p-2 bg-surface"
            />
            {fieldError('reason') && (
              <FieldError id="finance-validation-reason" message={fieldError('reason')} />
            )}
          </label>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={dismissDisabled}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={pending} disabled={pending}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldError({ id, message }: { id: string; message?: string }) {
  return (
    <p id={id} className="text-xs text-negative mt-1">
      {message}
    </p>
  );
}

function parseExactCents(raw: string, allowNegative: boolean): number | null {
  if (!raw || raw.trim() !== raw) return null;
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(raw);
  if (!match || (!allowNegative && match[1] === '-')) return null;
  const cents = BigInt(match[2] ?? '0') * 100n + BigInt((match[3] ?? '').padEnd(2, '0') || '0');
  const signed = match[1] === '-' ? -cents : cents;
  if (signed < -2147483648n || signed > 2147483647n) return null;
  return Number(signed);
}

function toUtcIso(raw: string): string {
  const parsed = new Date(`${raw}:00.000Z`);
  if (Number.isNaN(parsed.getTime())) throw new Error('Paid at must be a valid UTC date and time.');
  return parsed.toISOString();
}

function normalizePaidAt(raw: string | undefined): string | null {
  if (!raw) return null;
  try {
    return toUtcIso(raw);
  } catch {
    return null;
  }
}

function normalizeDateInput(raw: string | undefined): string | null {
  if (!raw) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const parsed = new Date(`${raw}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== raw) return null;
  return parsed.toISOString();
}

function payablesErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'The command failed.';
  const details = (error as Error & { details?: unknown }).details;
  if (details && typeof details === 'object' && 'error' in details) {
    const payload = details.error;
    if (payload && typeof payload === 'object') {
      const code = 'code' in payload && typeof payload.code === 'string' ? payload.code : null;
      const message =
        'message' in payload && typeof payload.message === 'string' ? payload.message : null;
      if (code && message && !message.includes(code)) return `${code}: ${message}`;
      if (code) return code;
    }
  }
  return error.message || 'The command failed.';
}

function payablesErrorCode(error: unknown): string | null {
  if (!(error instanceof Error)) return null;
  const details = (error as Error & { details?: unknown }).details;
  if (details && typeof details === 'object' && 'error' in details) {
    const payload = details.error;
    if (payload && typeof payload === 'object' && 'code' in payload) {
      return typeof payload.code === 'string' ? payload.code : null;
    }
  }
  return /^(CREDIT_[A-Z_]+|INVALID_CREDIT_SOURCE)/.exec(error.message)?.[1] ?? null;
}

/** Rows the settlement-coverage Needs-you item marks: everything that goes
 * dashed/provisional while Amazon hasn't closed the settlement window. */
const SETTLEMENT_MARKED_ROWS: readonly StatementRowKey[] = [
  'sales',
  'amazonFees',
  'proceeds',
  'netIncome',
];

/** Rows the attestation Needs-you item marks: the two expense ledgers it
 * covers, plus net income (which depends on both). */
const ATTESTATION_MARKED_ROWS: readonly StatementRowKey[] = ['warehouse', 'overhead', 'netIncome'];

/** Pure builder for the Needs-you list: expense confirmation, settlement
 * coverage, COGS replan, and sales-sync items, plus the per-cell markers
 * the income statement attaches to whichever cells each item explains.
 * Markers are assigned in item order, but only to items that actually
 * correspond to a table cell (replan and sales-sync items get none); a
 * cell explained by more than one item collects every marker ("1,2"). */
function buildFinanceNeedsYou(params: {
  monthKeys: readonly string[];
  activity: readonly FinanceSummarySettlementActivity[];
  attestations: readonly WarehouseExpenseAttestation[] | undefined;
  previews: readonly WarehouseExpensePeriodPreview[] | undefined;
  canAttest: boolean;
  settlementsCoverage: {
    status: 'complete' | 'backfilling' | 'pending' | 'unknown';
    complete: boolean;
  };
  salesCoverage: { status: 'complete' | 'backfilling' | 'pending' | 'unknown'; complete: boolean };
  sales: readonly {
    currency: string;
    months: readonly { month: string; revenueCents: number | null }[];
  }[];
  cogsReplan: CogsReplanState | null;
  isRequestingReplan: boolean;
  requestError: string | null;
  onOpenAttest: (month: string) => void;
  onRecompute: () => void;
}): { items: NeedsYouItem[]; markers: StatementMarkers; attestedFootnote: string | null } {
  const {
    monthKeys,
    activity,
    attestations,
    previews,
    canAttest,
    settlementsCoverage,
    salesCoverage,
    sales,
    cogsReplan,
    isRequestingReplan,
    requestError,
    onOpenAttest,
    onRecompute,
  } = params;

  const items: NeedsYouItem[] = [];
  const markers: Record<string, Partial<Record<StatementRowKey, string>>> = {};
  let nextMarker = 1;
  /** Assign one marker number to every (column, row) cell in `cells`,
   * appending to any marker(s) already on that cell ("1" -> "1,2"). */
  function attachMarker(
    cells: ReadonlyArray<{ column: string; row: StatementRowKey }>
  ): string | undefined {
    if (cells.length === 0) return undefined;
    const marker = String(nextMarker);
    nextMarker += 1;
    for (const { column, row } of cells) {
      const columnMarkers = markers[column] ?? (markers[column] = {});
      const existing = columnMarkers[row];
      columnMarkers[row] = existing ? `${existing},${marker}` : marker;
    }
    return marker;
  }
  function monthAndPeriodCells(
    month: string,
    rows: readonly StatementRowKey[]
  ): Array<{ column: string; row: StatementRowKey }> {
    return [
      ...rows.map((row) => ({ column: month, row })),
      ...rows.map((row) => ({ column: 'period', row })),
    ];
  }

  const attestedEntries: { month: string; attestedAtLabel: string }[] = [];

  if (canAttest) {
    const attestationByMonth = new Map((attestations ?? []).map((row) => [row.month, row]));
    const previewByMonth = new Map((previews ?? []).map((preview) => [preview.month, preview]));
    const statuses = expenseAttestationMonthStatuses(activity).filter((status) =>
      monthKeys.includes(status.month)
    );
    for (const status of statuses) {
      const record = attestationByMonth.get(status.month);
      if (status.blocked) {
        const stale = record != null;
        const preview = previewByMonth.get(status.month);
        const marker = attachMarker(monthAndPeriodCells(status.month, ATTESTATION_MARKED_ROWS));
        const monthLabel = expenseAttestationMonthLabel(status.month);
        items.push({
          id: `attest-${status.month}`,
          month: status.month,
          title: stale
            ? `Re-confirm ${monthLabel} expenses — they changed`
            : `Confirm ${monthLabel} expenses`,
          detail: preview
            ? `${warehouseEntrySummary(preview)} · ${operatingEntrySummary(preview)}. Confirm these are all of ${financeMonthName(status.month)}'s expenses.`
            : `Confirm these are all of ${financeMonthName(status.month)}'s expenses.`,
          marker,
          action: {
            label: stale ? 'Re-confirm' : 'Confirm expenses',
            variant: 'primary',
            onClick: () => onOpenAttest(status.month),
          },
        });
      } else if (record) {
        attestedEntries.push({
          month: status.month,
          attestedAtLabel: formatFeeCenterDate(record.attestedAt),
        });
      }
    }
  }

  if (settlementsCoverage.status !== 'complete') {
    let marker: string | undefined;
    if (settlementsCoverage.status === 'pending' || settlementsCoverage.status === 'unknown') {
      const lastMonth = monthKeys[monthKeys.length - 1];
      const affectedMonths =
        settlementsCoverage.status === 'pending' ? (lastMonth ? [lastMonth] : []) : [...monthKeys];
      const cells: Array<{ column: string; row: StatementRowKey }> = [];
      for (const month of affectedMonths) {
        for (const row of SETTLEMENT_MARKED_ROWS) cells.push({ column: month, row });
      }
      if (affectedMonths.length > 0) {
        for (const row of SETTLEMENT_MARKED_ROWS) cells.push({ column: 'period', row });
      }
      marker = attachMarker(cells);
    }
    if (settlementsCoverage.status === 'pending') {
      const lastMonth = monthKeys[monthKeys.length - 1];
      items.push({
        id: 'settlement-coverage',
        month: lastMonth,
        title: `Amazon has not closed ${lastMonth ? financeMonthName(lastMonth) : 'this period'} yet`,
        detail:
          'Totals appear once Amazon settles the remaining days. Marked figures show what Amazon has reported so far.',
        marker,
      });
    } else {
      items.push({
        id: 'settlement-coverage',
        title: 'Amazon settlement data is incomplete for this range',
        detail: financeCoverageCopy(settlementsCoverage, 'settlement')?.detail ?? '',
        marker,
      });
    }
  }

  if (cogsReplan && cogsReplan.pendingCount > 0) {
    const running = cogsReplan.runningSince != null;
    items.push({
      id: 'replan-pending',
      title: `Cost of goods sold has ${cogsReplan.pendingCount} change${cogsReplan.pendingCount === 1 ? '' : 's'} since the last recompute`,
      detail: requestError ? `It recomputes nightly. ${requestError}` : 'It recomputes nightly.',
      action: {
        label: running ? cogsReplanProgressLabel(cogsReplan.run) : 'Recompute now',
        variant: 'secondary',
        disabled: running || isRequestingReplan,
        pending: running,
        onClick: onRecompute,
      },
    });
  }
  if (cogsReplan && cogsReplan.lastError) {
    items.push({
      id: 'replan-error',
      title: 'Last recompute failed',
      detail: cogsReplan.lastError,
    });
  }

  if (!salesCoverage.complete) {
    for (const month of monthKeys) {
      const loaded = sales.some((currencySales) =>
        currencySales.months.some((entry) => entry.month === month && entry.revenueCents != null)
      );
      if (!loaded) {
        items.push({
          id: `sales-sync-${month}`,
          month,
          title: `Amazon sales for ${financeMonthName(month)} are still loading.`,
          detail: '',
        });
      }
    }
  }

  return {
    items,
    markers,
    attestedFootnote: financeAttestedMonthsFootnote(monthKeys, attestedEntries),
  };
}

/** Same sign convention as the statement's Amazon fees row: a charge
 * (positive netChargeCents) reads as a cost ("−$963.56"), a credit
 * (negative netChargeCents) reads as a positive line ("$16.23") — so every
 * line and subtotal in the disclosure sums the same way the statement does
 * (R2 item 1, 2026-09-02 cold-viewer review). */
function formatCategoryMovement(cents: number, currency: string): string {
  // Negating 0 yields -0, which would print as "-$0.00".
  return formatFinanceDelta(cents === 0 ? 0 : -cents, currency);
}

function CategoryMovements({
  activity,
  monthKeys,
  canOpenFeeCenter,
}: {
  activity: readonly FinanceSummarySettlementActivity[];
  monthKeys: readonly string[];
  canOpenFeeCenter: boolean;
}) {
  const rows = activity.flatMap((currencyActivity) =>
    currencyActivity.categories.map((category, index) => ({
      ...category,
      currency: currencyActivity.currency,
      key: `${currencyActivity.currency}-${category.month}-${category.category}-${index}`,
    }))
  );
  if (rows.length === 0) return null;

  // Group by month (chronological), each with a right-aligned subtotal.
  const monthOrder = [...new Set(rows.map((row) => row.month))].sort();
  const rowsByMonth = new Map<string, typeof rows>();
  for (const row of rows) {
    const existing = rowsByMonth.get(row.month);
    if (existing) existing.push(row);
    else rowsByMonth.set(row.month, [row]);
  }

  // Period total for the collapsed summary — the sum of every rendered
  // month's subtotal. Omitted (rather than shown partial) when a month in
  // the selected range has no category data at all.
  const periodComplete =
    monthKeys.length > 0 && monthKeys.every((month) => monthOrder.includes(month));
  const periodTotalsByCurrency = new Map<string, number>();
  for (const row of rows) {
    periodTotalsByCurrency.set(
      row.currency,
      (periodTotalsByCurrency.get(row.currency) ?? 0) + row.netChargeCents
    );
  }
  const periodTotalLabel = periodComplete
    ? [...periodTotalsByCurrency.entries()]
        .map(([currency, totalCents]) => formatCategoryMovement(totalCents, currency))
        .join(' · ')
    : null;

  return (
    <details>
      <summary className="cursor-pointer text-sm font-medium text-fg-primary">
        Where Amazon&rsquo;s cut went{periodTotalLabel ? ` · ${periodTotalLabel}` : ''}
      </summary>
      <div className="mt-3 space-y-4">
        {monthOrder.map((month) => {
          const monthRows = rowsByMonth.get(month)!;
          const subtotalsByCurrency = new Map<string, number>();
          for (const row of monthRows) {
            subtotalsByCurrency.set(
              row.currency,
              (subtotalsByCurrency.get(row.currency) ?? 0) + row.netChargeCents
            );
          }
          return (
            <div key={month}>
              <div className="flex items-baseline justify-between gap-3">
                <h4 className="text-sm font-semibold text-fg-primary">
                  {expenseAttestationMonthLabel(month)}
                </h4>
                <span className="text-sm font-semibold tabular-nums text-fg-primary">
                  {[...subtotalsByCurrency.entries()]
                    .map(([currency, totalCents]) => formatCategoryMovement(totalCents, currency))
                    .join(' · ')}
                </span>
              </div>
              <div className="mt-1 pl-4 divide-y divide-border-subtle">
                {monthRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex items-center justify-between gap-3 py-2 text-sm"
                  >
                    <span className="text-fg-secondary">{financeCategoryLabel(row.category)}</span>
                    <span className="tabular-nums text-fg-primary">
                      {formatCategoryMovement(row.netChargeCents, row.currency)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {canOpenFeeCenter && (
        <p className="text-xs text-fg-tertiary mt-2">
          <Link to="/app/finance/fee-center" className="text-accent-fg hover:underline">
            Line detail in Fee Center.
          </Link>
        </p>
      )}
    </details>
  );
}

function HowTheseNumbersAreBuilt({
  period,
  cogsReplanState,
  canOpenFeeCenter,
}: {
  period: FinancePeriod;
  cogsReplanState: CogsReplanState | null;
  canOpenFeeCenter: boolean;
}) {
  // R2 item 2 (A1): sources are fetched only once this disclosure has been
  // opened at least once — most visits never open it, so most visits never
  // pay for the request.
  const [open, setOpen] = useState(false);
  const sourcesQuery = useFinanceSummarySources(period, open);
  const cogsAsOf =
    cogsReplanState?.lastReconciledAt != null
      ? `Cost of goods sold as of ${formatRelativeTime(cogsReplanState.lastReconciledAt)}.`
      : 'No recompute has been recorded yet.';
  return (
    <details onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="cursor-pointer text-sm font-medium text-fg-primary">
        How these numbers are built
      </summary>
      <div className="mt-3 space-y-3 text-sm text-fg-secondary">
        <p>
          COGS uses sale purchase dates in UTC. Warehouse period expense uses recognized dates in
          UTC. Net income combines these date bases.
        </p>
        <p>{cogsAsOf}</p>
        <SourceEvidence
          sources={sourcesQuery.data?.cogs}
          isLoading={open && sourcesQuery.isLoading}
          isError={open && sourcesQuery.isError}
          error={sourcesQuery.error}
          onRetry={sourcesQuery.refetch}
        />
        <p className="text-xs text-fg-tertiary">
          {canOpenFeeCenter && (
            <>
              <Link to="/app/finance/fee-center" className="text-accent-fg hover:underline">
                Fee Center
              </Link>{' '}
              ·{' '}
            </>
          )}
          <Link to="/app/expenses" className="text-accent-fg hover:underline">
            Expenses
          </Link>
        </p>
      </div>
    </details>
  );
}

export function FinancePage() {
  const {
    data: me,
    isLoading: meLoading,
    isError: meError,
    error: meQueryError,
    refetch: refetchMe,
  } = useMe();
  const amazonEnabled = useModuleEnabled('amazon');
  const warehouseEnabled = useModuleEnabled('warehouse');
  const ordersEnabled = useModuleEnabled('orders');
  const canOpenFeeCenter = canAccessFeeCenter(me?.user.role, amazonEnabled);
  const [period, setPeriod] = useState<FinancePeriod>(() => defaultFinancePeriod());
  const [periodError, setPeriodError] = useState<string | null>(null);
  const hasTenantAccess =
    !meLoading && !!me && (me.user.role === 'owner' || me.user.role === 'operator');
  const payables = usePayablesController(hasTenantAccess && warehouseEnabled);
  const query = useFinanceSummary(period, hasTenantAccess && amazonEnabled);
  const isOwner = hasTenantAccess && me?.user.role === 'owner';
  const cogsReplanQuery = useCogsReplanState(hasTenantAccess && amazonEnabled);
  const requestCogsReplan = useRequestCogsReplan();
  const [cogsReplanRequestError, setCogsReplanRequestError] = useState<string | null>(null);

  async function handleCogsReplanRecompute() {
    setCogsReplanRequestError(null);
    try {
      await requestCogsReplan.mutateAsync();
    } catch (err) {
      setCogsReplanRequestError(
        err instanceof ApiCallError && err.status === 409
          ? 'A recompute is already running.'
          : err instanceof Error
            ? err.message
            : 'Could not start recompute.'
      );
    }
  }
  const expenseAttestationsQuery = useWarehousePeriodExpenseAttestations(
    isOwner && warehouseEnabled && amazonEnabled
  );
  const attestExpensePeriod = useAttestWarehousePeriodExpenses();
  const [pendingAttestMonth, setPendingAttestMonth] = useState<string | null>(null);
  // R1 item 4 follow-up (2026-09-02) — one shared preview fetch for every
  // month in the selected period, feeding both the Needs-you attestation
  // items and the confirm dialog (owner-only control, but the preview read
  // itself isn't owner-gated).
  const expensePeriodPreviewMonths = [
    ...new Set(
      (query.data?.settlementActivity ?? []).flatMap((activity) =>
        activity.monthly.map((month) => month.month)
      )
    ),
  ].sort();
  const expensePeriodPreviewQuery = useWarehouseExpensePeriodPreview(
    expensePeriodPreviewMonths,
    hasTenantAccess && warehouseEnabled && amazonEnabled && expensePeriodPreviewMonths.length > 0
  );

  if (meLoading) {
    return <AccessLoading />;
  }

  if (meError) return <AccessSessionError error={meQueryError} retry={refetchMe} />;

  if (!me) return <AccessUnauthenticated />;

  if (me?.user.role === 'warehouse') {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              Warehouse accounts can&rsquo;t access Finance.{' '}
              <Link to="/app/receiving" className="text-accent-fg hover:underline">
                Go to Receiving
              </Link>
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  if (me.user.role !== 'owner' && me.user.role !== 'operator') {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              This account is not granted access to Finance.
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  const cogsReplanState: CogsReplanState | null =
    query.data?.cogsReplan ?? cogsReplanQuery.data?.state ?? null;
  const coverage = query.data?.coverage;
  const monthKeys = financePeriodMonthKeys(period);
  const rangeLabel = financePeriodRangeLabel(monthKeys);

  let needsYou: NeedsYouItem[] = [];
  let markers: StatementMarkers = {};
  let attestedFootnote: string | null = null;
  if (amazonEnabled && query.data && coverage) {
    const built = buildFinanceNeedsYou({
      monthKeys,
      activity: query.data.settlementActivity,
      attestations: expenseAttestationsQuery.data,
      previews: expensePeriodPreviewQuery.data?.previews,
      canAttest: isOwner && warehouseEnabled,
      settlementsCoverage: coverage.settlements,
      salesCoverage: coverage.sales,
      sales: query.data.sales,
      cogsReplan: cogsReplanState,
      isRequestingReplan: requestCogsReplan.isPending,
      requestError: cogsReplanRequestError,
      onOpenAttest: (month) => setPendingAttestMonth(month),
      onRecompute: () => void handleCogsReplanRecompute(),
    });
    needsYou = built.items;
    markers = built.markers;
    attestedFootnote = built.attestedFootnote;
  }
  const statusSentence =
    amazonEnabled && query.data ? financeStatusSentence(rangeLabel, needsYou) : null;

  const pendingAttestRecord = pendingAttestMonth
    ? (expenseAttestationsQuery.data ?? []).find((row) => row.month === pendingAttestMonth)
    : undefined;
  const pendingAttestPreview = pendingAttestMonth
    ? (expensePeriodPreviewQuery.data?.previews ?? []).find(
        (preview) => preview.month === pendingAttestMonth
      )
    : undefined;

  const footerLinks: Array<{ to: string; label: string }> = [];
  if (ordersEnabled) footerLinks.push({ to: '/app/orders', label: 'Claims on Orders' });
  if (canOpenFeeCenter) footerLinks.push({ to: '/app/finance/fee-center', label: 'Fee Center' });

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-6">
          <div className="flex items-start justify-between gap-5 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">FINANCE</p>
              <h1 className="font-display text-4xl sm:text-[44px] font-medium leading-[1.08] tracking-tight mt-3">
                Income statement
              </h1>
              {statusSentence && (
                <p className="text-sm text-fg-secondary mt-3 max-w-2xl">{statusSentence}</p>
              )}
            </div>
            <CompleteMonthRangeControls
              period={period}
              error={periodError}
              labelPrefix="Finance"
              onChange={(field, value) => {
                const next = transitionFinancePeriod(period, field, value);
                setPeriodError(next.error);
                if (!next.error) setPeriod(next.period);
              }}
            />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-8 space-y-8">
        {!amazonEnabled && <AmazonDisabled />}
        {amazonEnabled && query.isError && (
          <Card>
            <p className="text-sm text-negative">
              Could not load Finance:{' '}
              {query.error instanceof Error ? query.error.message : 'request failed'}
            </p>
            <p className="text-xs text-fg-tertiary mt-1">
              Try another complete UTC calendar-month period.
            </p>
          </Card>
        )}
        {amazonEnabled && query.isLoading && (
          <div className="space-y-8" aria-hidden="true">
            <section>
              <div className="mb-3">
                <SkeletonLine className="h-4.5 w-64" />
                <SkeletonLine className="h-3.5 w-full max-w-xl mt-2" />
              </div>
              <div className="grid gap-4 min-w-0">
                <Card>
                  <div className="space-y-3">
                    <SkeletonLine className="h-4" />
                    <SkeletonLine className="h-4" />
                    <SkeletonLine className="h-4" />
                    <SkeletonLine className="h-4" />
                  </div>
                </Card>
              </div>
            </section>
            <section>
              <div className="mb-3">
                <SkeletonLine className="h-4.5 w-40" />
                <SkeletonLine className="h-3.5 w-full max-w-md mt-2" />
              </div>
              <Card>
                <div className="space-y-3">
                  <SkeletonLine className="h-4" />
                  <SkeletonLine className="h-4" />
                  <SkeletonLine className="h-4" />
                </div>
              </Card>
            </section>
          </div>
        )}
        {amazonEnabled && query.data && (
          <>
            <section aria-label="Income statement" className="space-y-6">
              {query.data.settlementActivity.length === 0 ? (
                <Card>
                  <p className="text-sm text-fg-secondary">
                    No Amazon activity found for this period.
                  </p>
                </Card>
              ) : (
                query.data.settlementActivity.map((activity) => (
                  <IncomeStatement
                    key={activity.currency}
                    activity={activity}
                    monthKeys={monthKeys}
                    markers={markers}
                    showCaption={query.data.settlementActivity.length > 1}
                  />
                ))
              )}
              {attestedFootnote && <p className="text-xs text-fg-tertiary">{attestedFootnote}</p>}
            </section>

            <BusinessPositionPanel />

            <NeedsYou items={needsYou} />

            <CategoryMovements
              activity={query.data.settlementActivity}
              monthKeys={monthKeys}
              canOpenFeeCenter={canOpenFeeCenter}
            />

            <PayoutSection payouts={query.data.payouts} canOpenFeeCenter={canOpenFeeCenter} />
          </>
        )}

        <PayablesWorkspace enabled={warehouseEnabled} controller={payables} />

        {amazonEnabled && query.data && (
          <HowTheseNumbersAreBuilt
            period={period}
            cogsReplanState={cogsReplanState}
            canOpenFeeCenter={canOpenFeeCenter}
          />
        )}

        {footerLinks.length > 0 && (
          <p className="text-xs text-fg-tertiary">
            Also:{' '}
            {footerLinks.map((link, index) => (
              <span key={link.to}>
                {index > 0 && ' · '}
                <Link to={link.to} className="text-accent-fg hover:underline">
                  {link.label}
                </Link>
              </span>
            ))}
          </p>
        )}
      </main>
      <AttestationDialog
        month={pendingAttestMonth}
        stale={pendingAttestRecord != null}
        preview={pendingAttestPreview}
        onClose={() => setPendingAttestMonth(null)}
        onConfirm={(month) => attestExpensePeriod.mutateAsync({ month, attested: true })}
      />
    </AppShell>
  );
}

function AccessLoading() {
  return (
    <AppShell>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16" aria-live="polite">
        <Card>
          <p className="text-sm text-fg-secondary">Checking finance access…</p>
        </Card>
      </main>
    </AppShell>
  );
}

function AccessSessionError({ error, retry }: { error?: unknown; retry?: () => unknown }) {
  return (
    <AppShell>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
        <Card>
          <p className="text-sm text-negative">
            Could not verify Finance access:{' '}
            {error instanceof Error ? error.message : 'request failed'}
          </p>
          {retry && (
            <Button variant="ghost" size="sm" className="mt-3" onClick={() => void retry()}>
              Retry
            </Button>
          )}
        </Card>
      </main>
    </AppShell>
  );
}

function AccessUnauthenticated() {
  return (
    <AppShell>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
        <Card>
          <p className="text-sm text-fg-secondary">
            Your session is not signed in.{' '}
            <Link to="/signin" className="text-accent-fg hover:underline">
              Sign in to continue.
            </Link>
          </p>
        </Card>
      </main>
    </AppShell>
  );
}

function AmazonDisabled() {
  return (
    <Card>
      <div className="flex items-center gap-2">
        <Badge variant="neutral">Amazon module off</Badge>
        <h2 className="text-sm font-medium text-fg-primary">Finance is waiting for Amazon data</h2>
      </div>
      <p className="text-sm text-fg-secondary mt-3">
        Connect the Amazon module to see settlement revenue, charges, credits, and payouts. No
        amounts are estimated here.
      </p>
    </Card>
  );
}

/** "Warehouse: $46.80 from 2 bills" / "Warehouse: none entered" — line 2 of
 * a month's attestation row (R1 item 4 follow-up, 2026-09-02, Faraz: "can I
 * see a summary ... instead of going into details?"). Line-count dropped
 * (R2 item 4, 2026-09-02 cold-viewer review) — the bill count is the useful
 * fact; a line count reads as noise. */
function warehouseEntrySummary(preview: WarehouseExpensePeriodPreview): string {
  if (preview.warehouse.count === 0) return 'Warehouse: none entered';
  const billCount = new Set(preview.warehouse.items.map((item) => item.invoiceId)).size;
  return (
    `Warehouse: ${formatFinanceCurrency(preview.warehouse.totalCents, preview.currency)} from ` +
    `${billCount} bill${billCount === 1 ? '' : 's'}`
  );
}

/** "Overhead: $650.00" / "Overhead: none entered". */
function operatingEntrySummary(preview: WarehouseExpensePeriodPreview): string {
  if (preview.operating.count === 0) return 'Overhead: none entered';
  return `Overhead: ${formatFinanceCurrency(preview.operating.totalCents, preview.currency)}`;
}

/** Warehouse + operating combined, for the row summary line and the confirm
 * dialog (R1 item 4 follow-up, 2026-09-02, Faraz: "the Month attestation
 * rows show per-ledger figures but no combined total"). */
function expensePeriodPreviewTotalCents(preview: WarehouseExpensePeriodPreview): number {
  return preview.warehouse.totalCents + preview.operating.totalCents;
}

/** "Total $696.80". */
function expensePeriodPreviewTotalSummary(preview: WarehouseExpensePeriodPreview): string {
  return `Total ${formatFinanceCurrency(expensePeriodPreviewTotalCents(preview), preview.currency)}`;
}

/** One itemized table inside the attestation confirm dialog — warehouse or
 * operating lines, description + amount, with a total row. */
function ExpensePeriodPreviewTable({
  title,
  totalLabel,
  totalCents,
  currency,
  rows,
}: {
  title: string;
  totalLabel: string;
  totalCents: number;
  currency: string;
  rows: ReadonlyArray<{ id: string; description: string; amountCents: number }>;
}) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] text-fg-tertiary mb-1">{title}</p>
      {rows.length === 0 ? (
        <p className="text-xs text-fg-tertiary">None entered.</p>
      ) : (
        <div className="divide-y divide-border-subtle max-h-40 overflow-y-auto">
          {rows.map((row) => (
            <div key={row.id} className="flex items-center justify-between gap-3 py-1.5 text-xs">
              <span className="text-fg-secondary min-w-0 break-words">{row.description}</span>
              <span className="tabular-nums shrink-0">
                {formatFinanceCurrency(row.amountCents, currency)}
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-fg-tertiary mt-1">
        {totalLabel}: {formatFinanceCurrency(totalCents, currency)}
      </p>
    </div>
  );
}

/** The attestation confirm dialog opened from a Needs-you "Attest"/
 * "Re-attest" action. Lifted out of the old per-currency attestation
 * section (R1 item 4) so it's a single page-level dialog driven by
 * FinancePage's `pendingAttestMonth` state, matching D1 (one attestation
 * declaration per month, not per currency). */
function AttestationDialog({
  month,
  stale,
  preview,
  onClose,
  onConfirm,
}: {
  month: string | null;
  stale: boolean;
  preview: WarehouseExpensePeriodPreview | undefined;
  onClose: () => void;
  onConfirm: (month: string) => Promise<unknown>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSubmitting(false);
    setError(null);
  }, [month]);

  if (!month) return null;
  const dialogCopy = expenseAttestationDialogCopy(month, stale);

  function cancel() {
    if (submitting) return;
    onClose();
  }

  async function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(month!);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirmation failed.');
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={month != null} onOpenChange={(open) => !open && cancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogCopy.title}</DialogTitle>
          <DialogDescription>{dialogCopy.body}</DialogDescription>
        </DialogHeader>
        {preview && (
          <div className="space-y-4">
            <ExpensePeriodPreviewTable
              title="Warehouse expenses"
              totalLabel="Warehouse total"
              totalCents={preview.warehouse.totalCents}
              currency={preview.currency}
              rows={preview.warehouse.items.map((item) => ({
                id: item.id,
                description: item.invoiceLabel
                  ? `${item.description} · ${item.invoiceLabel}`
                  : item.description,
                amountCents: item.amountCents,
              }))}
            />
            <ExpensePeriodPreviewTable
              title="Overhead expenses"
              totalLabel="Overhead total"
              totalCents={preview.operating.totalCents}
              currency={preview.currency}
              rows={preview.operating.items.map((item) => ({
                id: item.id,
                description:
                  `${item.description} · ${formatOperatingExpenseCategory(item.category)}` +
                  (item.recurring ? ' · recurring' : ''),
                amountCents: item.amountCents,
              }))}
            />
            <p className="text-sm font-medium text-fg-primary">
              {expensePeriodPreviewTotalSummary(preview)}
            </p>
            <p className="text-sm text-fg-primary">
              Confirming means this is every expense for {financeMonthName(month)}.
            </p>
          </div>
        )}
        {error && <p className="text-sm text-negative">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" size="sm" disabled={submitting} onClick={cancel}>
            Cancel
          </Button>
          <Button size="sm" disabled={submitting} onClick={() => void confirm()}>
            {submitting ? 'Confirming…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** R2 item 4 (docs/plans/2026-09-03-business-position.md): "every cent I
 * invested" — collapsed by default; the hook only fetches once opened, so
 * a visit that never opens this never pays for the extra request. Invested
 * nests its own disclosure for the six-way breakdown. */
function BusinessPositionPanel() {
  const [open, setOpen] = useState(false);
  const query = useBusinessPosition(open);

  return (
    <details onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="cursor-pointer text-sm font-medium text-fg-primary">
        Business position
      </summary>
      {open && (
        <div className="mt-3">
          {query.isPending ? (
            <Card>
              <div className="space-y-3">
                <SkeletonLine className="h-4" />
                <SkeletonLine className="h-4" />
                <SkeletonLine className="h-4" />
              </div>
            </Card>
          ) : query.isError ? (
            <Card>
              <p className="text-sm text-negative">Could not load the business position.</p>
              <p className="text-xs text-fg-tertiary mt-1">
                {query.error instanceof Error ? query.error.message : 'Request failed.'}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => void query.refetch()}
              >
                Retry
              </Button>
            </Card>
          ) : (
            <BusinessPositionLines data={query.data} />
          )}
        </div>
      )}
    </details>
  );
}

function BusinessPositionLine({
  label,
  cents,
  currency,
  grade,
}: {
  label: string;
  cents: number;
  currency: string;
  grade: 'settlement' | 'estimate';
}) {
  const gradeLabel = businessPositionGradeLabel(grade);
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-fg-secondary flex items-center gap-2">
        {label}
        {gradeLabel && <Badge variant="neutral">{gradeLabel}</Badge>}
      </span>
      <span className="text-sm font-medium tabular-nums">
        {formatFinanceCurrency(cents, currency)}
      </span>
    </div>
  );
}

function BusinessPositionLines({ data }: { data: BusinessPositionResponse }) {
  const [investedOpen, setInvestedOpen] = useState(false);
  const { currency, lines } = data;
  const breakdown = lines.invested.breakdown;
  return (
    <Card>
      <div className="divide-y divide-border-subtle">
        <details onToggle={(event) => setInvestedOpen(event.currentTarget.open)}>
          <summary className="cursor-pointer list-none">
            <BusinessPositionLine
              label="Invested"
              cents={lines.invested.cents}
              currency={currency}
              grade={lines.invested.grade}
            />
          </summary>
          {investedOpen && (
            <div className="pb-2 pl-3 space-y-1">
              <BusinessPositionLine
                label="Vendor invoices"
                cents={breakdown.vendorCents}
                currency={currency}
                grade="settlement"
              />
              <BusinessPositionLine
                label="Landed charges"
                cents={breakdown.chargesCents}
                currency={currency}
                grade="settlement"
              />
              <BusinessPositionLine
                label="Credit memos"
                cents={breakdown.creditsCents}
                currency={currency}
                grade="settlement"
              />
              <BusinessPositionLine
                label="Warehouse bills"
                cents={breakdown.warehouseCents}
                currency={currency}
                grade="settlement"
              />
              <BusinessPositionLine
                label="Overhead"
                cents={breakdown.overheadCents}
                currency={currency}
                grade="settlement"
              />
              <BusinessPositionLine
                label="Amazon fees and charges"
                cents={breakdown.amazonFeesCents}
                currency={currency}
                grade="settlement"
              />
            </div>
          )}
        </details>
        <BusinessPositionLine
          label="Returned by Amazon"
          cents={lines.returned.cents}
          currency={currency}
          grade={lines.returned.grade}
        />
        <BusinessPositionLine
          label="Still in inventory"
          cents={lines.inventory.cents}
          currency={currency}
          grade={lines.inventory.grade}
        />
        {lines.inventory.unitsSoldWithoutLineage > 0 && (
          <p className="text-xs text-fg-tertiary pb-1.5">
            {lines.inventory.unitsSoldWithoutLineage} sold units not yet costed are still counted
            as stock
          </p>
        )}
        <BusinessPositionLine
          label="Realized margin"
          cents={lines.realizedMargin.cents}
          currency={currency}
          grade={lines.realizedMargin.grade}
        />
        <BusinessPositionLine
          label="Position"
          cents={lines.position.cents}
          currency={currency}
          grade={lines.position.grade}
        />
      </div>
      <p className="text-xs text-fg-tertiary mt-3">
        {businessPositionSinceAsOfLabel(data.since, data.asOf)}
        {data.includesOpenMonth && ' · Includes the current open month'}
      </p>
      <p className="text-xs text-fg-secondary mt-1">
        Every dollar spent on inventory, warehousing, overhead, and Amazon fees, set against every
        dollar of Amazon sales before fees, plus what is still in stock.
      </p>
    </Card>
  );
}

/** R2 item 2 (A1): sources come from useFinanceSummarySources now, not from
 * the settlement activity the summary used to embed them in — so this is no
 * longer per-currency. COGS coverage only ever admits USD cost layers (see
 * isSupportedCogsCurrency on the API side), so every row here is USD in
 * practice; a currency-less display would only ever hide that fact, not add
 * a real multi-currency case (ASSUMPTION). */
function SourceEvidence({
  sources,
  isLoading,
  isError,
  error,
  onRetry,
}: {
  sources: FinanceSummarySource[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry: () => unknown;
}) {
  return (
    <details className="mt-5">
      <summary className="cursor-pointer text-sm font-medium text-fg-primary">
        COGS source evidence{sources === undefined ? '' : ` (${sources.length})`}
      </summary>
      {isLoading ? (
        <div className="mt-3 space-y-2">
          <SkeletonLine className="h-16 w-full rounded-lg" />
          <SkeletonLine className="h-16 w-full rounded-lg" />
        </div>
      ) : isError ? (
        <div className="mt-2 space-y-1">
          <p className="text-xs text-negative">Could not load the cost evidence.</p>
          <p className="text-xs text-fg-tertiary">
            {error instanceof Error ? error.message : 'Request failed.'}
          </p>
          <Button variant="ghost" size="sm" className="mt-1" onClick={() => void onRetry()}>
            Retry
          </Button>
        </div>
      ) : sources === undefined ? (
        <p className="text-xs text-fg-tertiary mt-2">
          COGS source evidence is unavailable for legacy data.
        </p>
      ) : sources.length === 0 ? (
        <p className="text-xs text-fg-tertiary mt-2">No COGS source rows were supplied.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {sources.map((source, index) => (
            <SourceRow key={`${source.consumptionId}-${index}`} source={source} currency="USD" />
          ))}
        </div>
      )}
    </details>
  );
}

function SourceRow({ source, currency }: { source: FinanceSummarySource; currency: string }) {
  const fields: Array<[string, string]> = [
    ['Sale', source.saleId],
    ['Consumption', source.consumptionId],
    ['Shipment', source.shipmentId],
    ['Shipment line', source.shipmentLineId],
    ['FBA allocation', source.fbaAllocationId],
    ['Lot', source.lotId],
    ['Cost layer', source.costLayerId ?? '—'],
    ['Warehouse allocation', source.warehouseAllocationId ?? '—'],
    ['Charge', source.chargeId ?? '—'],
    ['Invoice', source.invoiceId ?? '—'],
    ['Invoice line', source.invoiceLineId ?? '—'],
    ['Prep batch', source.prepReference ?? '—'],
    ['Prep box', source.prepBoxReference ?? '—'],
    ['Receipt', source.receiptId ?? '—'],
    ['PO', source.poReference ?? '—'],
  ];
  const warehouseLink = financeWarehouseLinkCopy(source);
  return (
    <div className="rounded-lg border border-border-subtle p-3 text-xs space-y-1">
      <p className="font-medium text-fg-primary">
        {financeSourceEventLabel(source.event)} · {financeSourceCostLabel(source.costLayerId)}
      </p>
      {fields.map(([label, value]) => (
        <p key={label} className="text-fg-secondary">
          {label}: <span className="font-mono text-fg-primary">{value}</span>
        </p>
      ))}
      <p className="text-fg-secondary">
        Vendor units: {formatFinanceSourceUnits(source.vendorUnits)}
      </p>
      <p className="text-fg-secondary">
        Source cost: {formatFinanceSourceCost(source.vendorCostCents, currency)}
      </p>
      <p className="text-fg-secondary">{financeDateBasisLabel('purchase_date_utc')}</p>
      <p className="text-fg-secondary">{financeDateBasisLabel('recognized_at_utc')}</p>
      {warehouseLink && <p className="text-warning">{warehouseLink}</p>}
      <p className="text-fg-tertiary">Purchase UTC: {source.purchaseDate}</p>
      <p className="text-fg-tertiary">Recognized UTC: {source.recognizedAt}</p>
    </div>
  );
}

function PayoutSection({
  payouts,
  canOpenFeeCenter,
}: {
  payouts: FinanceSummaryPayout[];
  canOpenFeeCenter: boolean;
}) {
  // Faraz, 2026-09-01: "make payout collapsable. Plus I am not sure I
  // understand the whole area. It is confusing." Collapsed by default —
  // the header line and the plain-language sentence below it are the
  // whole story for most visits; cards (and their integrity-audit detail)
  // are opt-in.
  const [open, setOpen] = useState(() => loadPayoutsOpen());
  useEffect(() => {
    savePayoutsOpen(open);
  }, [open]);

  const currencySummaries = payoutSectionSummary(payouts);
  const totalCount = payouts.length;
  const headerAmount =
    currencySummaries.length === 1
      ? ` · ${formatFinanceCurrency(currencySummaries[0]!.totalCents, currencySummaries[0]!.currency)}`
      : '';

  return (
    <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary id="payouts-heading" className="cursor-pointer text-sm font-medium text-fg-primary">
        Payouts · {totalCount} settlement{totalCount === 1 ? '' : 's'}
        {headerAmount}
      </summary>
      {open && (
        <div id="payouts-content" className="mt-3 space-y-3">
          {currencySummaries.length > 1 && (
            <p className="text-sm text-fg-secondary">
              {currencySummaries
                .map((entry) => formatFinanceCurrency(entry.totalCents, entry.currency))
                .join(' · ')}
            </p>
          )}
          <p className="text-sm text-fg-secondary">
            Each Amazon settlement is a statement for about two weeks of sales minus fees; Amazon
            deposits the net amount to your bank. Figures come from Amazon&apos;s settlement report,
            not from your bank.
          </p>
          {payouts.length === 0 ? (
            <Card>
              <p className="text-sm text-fg-secondary">
                No dated Amazon settlements were found for this period.
              </p>
            </Card>
          ) : (
            payouts.map((payout) => (
              <PayoutCard
                key={payout.settlementId}
                payout={payout}
                canOpenFeeCenter={canOpenFeeCenter}
              />
            ))
          )}
        </div>
      )}
    </details>
  );
}

function PayoutCard({
  payout,
  canOpenFeeCenter,
}: {
  payout: FinanceSummaryPayout;
  canOpenFeeCenter: boolean;
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const statusVariant =
    payout.reconciliationStatus === 'reconciled'
      ? 'positive'
      : payout.reconciliationStatus === 'partial'
        ? 'warning'
        : 'neutral';
  const detailId = `payout-detail-${payout.settlementId}`;
  return (
    <Card>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-medium text-fg-primary">
            {payout.periodStart && payout.periodEnd
              ? `Settlement ${formatFeeCenterDate(payout.periodStart)} – ${formatFeeCenterDate(payout.periodEnd)}`
              : 'Settlement · period not supplied'}
          </h3>
          <p className="text-xs text-fg-tertiary mt-1 font-mono">{payout.settlementId}</p>
        </div>
        <Badge variant={statusVariant}>
          {payoutStatusPlainLabel(
            payout.reconciliationStatus,
            payout.unexplainedDeltaCents,
            payout.currency
          )}
        </Badge>
      </div>
      <div className="mt-5">
        <p className="font-display text-2xl text-fg-primary tabular-nums break-words">
          {formatFinanceCurrency(payout.settlementTotalCents, payout.currency)}
        </p>
        <p className="text-xs text-fg-tertiary mt-1">
          {payout.depositDate
            ? `Deposited ${formatFeeCenterDate(payout.depositDate)}`
            : 'Deposit date not supplied'}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setDetailOpen((value) => !value)}
        aria-expanded={detailOpen}
        aria-controls={detailId}
        className="mt-4 text-sm text-accent-fg hover:underline"
      >
        {detailOpen ? 'Hide detail' : 'Show detail'}
      </button>
      {detailOpen && (
        <div id={detailId} className="mt-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Metric
              label="Statement total"
              value={formatFinanceCurrency(payout.settlementTotalCents, payout.currency)}
            />
            <Metric
              label="Line items we have"
              value={formatFinanceDelta(payout.explainedLedgerNetCents, payout.currency)}
            />
            <Metric
              label="Difference"
              value={formatFinanceDelta(payout.unexplainedDeltaCents, payout.currency)}
            />
          </div>
          <p className="text-xs text-fg-secondary mt-5">
            {payoutDateCopy(payout.depositDate)} · {financeSettlementHeaderCopy()}
          </p>
          <p className="text-sm text-fg-secondary mt-2">
            {financePayoutReconciliationCopy(
              payout.reconciliationStatus,
              payout.unexplainedDeltaCents,
              payout.currency
            )}
            {payout.reconciliationStatus === 'partial' && canOpenFeeCenter ? (
              <>
                {' '}
                <Link to="/app/finance/fee-center" className="text-accent-fg hover:underline">
                  Review in Fee Center.
                </Link>
              </>
            ) : null}
          </p>
        </div>
      )}
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-fg-tertiary">{label}</p>
      <p className="font-display text-2xl text-fg-primary mt-1 tabular-nums break-words">{value}</p>
    </div>
  );
}
